import React from 'react';

import { GiroPaymentManagerSummaryScreen } from 'containers/GiroPaymentManager/GiroPaymentManagerSummaryScreen';
import { GiroPaymentManagerClientSearchScreen } from 'containers/GiroPaymentManager/GiroPaymentManagerClientSearchScreen';
import { GiroPaymentManagerRateEditorScreen } from 'containers/GiroPaymentManager/GiroPaymentManagerRateEditorScreen';
import { AsyncBoundaryComponent } from 'utils/asyncBoundary/asyncBoundaryComponents';
import { ModalLayerComponent } from 'design/modalLayer';
import { SidebarModalWindowComponent } from 'design/sidebarModal';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { useGiroPaymentManager } from 'hooks/useGiroPaymentManager';

type Props = {
  giroPaymentId: number;
  onViewClientDetails(clientId: number): void;
  onPaymentCancelled(): void;
  onPaymentConfirmed(): void;
  onClientEdited(): void;
  onRateEdited(): void;
};

export const GiroPaymentManager = (props: Props) => {
  const {
    giroPaymentId,
    onViewClientDetails,
    onPaymentCancelled,
    onPaymentConfirmed,
    onClientEdited,
    onRateEdited,
  } = props;

  const dispatch = useAppDispatcher();

  const { screen } = useGiroPaymentManager();

  const goToSummaryScreen = React.useCallback(() => {
    dispatch({
      name: 'GiroPaymentManager__ScreenChanged',
      nextScreen: 'SUMMARY',
    });
  }, [dispatch]);

  const openClientSearchScreen = React.useCallback(() => {
    dispatch({
      name: 'GiroPaymentManager__ScreenChanged',
      nextScreen: 'CLIENT_SEARCH',
    });
  }, [dispatch]);

  const goToRateEditorScreen = React.useCallback(() => {
    dispatch({
      name: 'GiroPaymentManager__ScreenChanged',
      nextScreen: 'RATE_EDITOR',
    });
  }, [dispatch]);

  const openClientEditScreen = React.useCallback(() => {
    dispatch({
      name: 'GiroPaymentManager__ScreenChanged',
      nextScreen: 'CLIENT_SEARCH',
    });
  }, [dispatch]);

  const close = React.useCallback(() => {
    dispatch({
      name: 'GiroPaymentManager__Closed',
    });
  }, [dispatch]);

  const handleClientEdited = React.useCallback(() => {
    goToSummaryScreen();
    onClientEdited();
  }, [goToSummaryScreen, onClientEdited]);

  const handleRateEdited = React.useCallback(() => {
    goToSummaryScreen();
    onRateEdited();
  }, [goToSummaryScreen, onRateEdited]);

  const screenNode = React.useMemo(() => {
    switch (screen) {
      case 'SUMMARY':
        return (
          <GiroPaymentManagerSummaryScreen
            giroPaymentId={giroPaymentId}
            onPaymentCancelled={onPaymentCancelled}
            onPaymentConfirmed={onPaymentConfirmed}
            onSearchClient={openClientSearchScreen}
            onEditRate={goToRateEditorScreen}
            onViewClientDetails={onViewClientDetails}
            onEditClient={openClientEditScreen}
          />
        );
      case 'CLIENT_SEARCH':
        return (
          <GiroPaymentManagerClientSearchScreen
            paymentId={giroPaymentId}
            onCancel={goToSummaryScreen}
            onEdited={handleClientEdited}
          />
        );
      case 'RATE_EDITOR':
        return (
          <GiroPaymentManagerRateEditorScreen
            paymentId={giroPaymentId}
            onCancel={goToSummaryScreen}
            onEdited={handleRateEdited}
          />
        );
      default:
        return null;
    }
  }, [
    giroPaymentId,
    goToRateEditorScreen,
    goToSummaryScreen,
    handleClientEdited,
    handleRateEdited,
    onPaymentCancelled,
    onPaymentConfirmed,
    onViewClientDetails,
    openClientEditScreen,
    openClientSearchScreen,
    screen,
  ]);

  const closeHandler =
    screen === 'SUMMARY' || screen === 'CLIENT_SEARCH' ? close : undefined;

  return (
    <ModalLayerComponent theme="transparent">
      <SidebarModalWindowComponent
        onMisclick={close}
        onEscape={close}
        onClose={closeHandler}
      >
        <AsyncBoundaryComponent fallback={null}>
          {screenNode}
        </AsyncBoundaryComponent>
      </SidebarModalWindowComponent>
    </ModalLayerComponent>
  );
};
